/**
 * ACT2-1124: the warehouse accounts of the workspaces that Lookout can read (Settings › General, D18), for the
 * Warehouses list of a sibling workspace, and their one-click Connect.
 *
 * Connect runs two writes in order: `accountCopies.pick` copies the account into this workspace (config-backend copies
 * the secret, and a later pick reuses the copy), then `settings.warehouses.adoptAccountAsNewConnection` connects the
 * copy. Each write invalidates what it changes. After the copy, the copy is one of this workspace's own accounts, so
 * a refusal of the connect step renders on the copy's own row.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { orpcErrorMessage } from '@/_shared/lib/orpc-errors';

import type { OrganizationAccountOption } from '../warehouseAccount/warehouseList';

export interface OrganizationWarehouseAccounts {
  options: OrganizationAccountOption[];
  isLoading: boolean;
  /** Lookout could not read the organization's accounts; the list then shows only this workspace's own. */
  unavailable: string | null;
  /**
   * How many workspaces the readable list of Settings › General holds, once the list is read. Zero means that no
   * account of another workspace can show.
   */
  readableWorkspaceCount: number | null;
  /** Starts Connect. A Redshift IAM account first asks for confirmation (`confirming`). */
  connect: (option: OrganizationAccountOption) => void;
  /** The account that Connect is copying or connecting now, or null. */
  connectingAccountId: string | null;
  /** Why the copy of this account failed, or null. Only the row it was for renders it. */
  copyError: (accountId: string) => string | null;
  /** The Redshift IAM account waiting for the person to confirm its copy. */
  confirming: OrganizationAccountOption | null;
  confirm: () => void;
  cancelConfirm: () => void;
  copyPending: boolean;
}

/** Stable, so the list's memo does not rebuild on every render while the query has no data. */
const NO_OPTIONS: OrganizationAccountOption[] = [];

/** One copy at a time, so one fixed toast id. */
const COPYING_TOAST_ID = 'organization-account-copy';

const COPYING_MESSAGE =
  'Copying the account into this workspace. RudderStack checks a warehouse account first, which can take a minute or two.';

export function useOrganizationWarehouseAccounts(options: {
  enabled: boolean;
  /** The connect step: `useAdoptAccounts().adoptAsNewConnection`. */
  connectCopy: (input: { accountId: string }) => void;
  /** The account id the connect step is running for, from `useAdoptAccounts().adoptingNewAccountId`. */
  connectingCopyId: string | null;
}): OrganizationWarehouseAccounts {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const query = useQuery({
    ...orpc.accountCopies.listOptions.queryOptions({ input: { kind: 'warehouse' } }),
    enabled: options.enabled,
  });
  const [copyFailure, setCopyFailure] = useState<{ accountId: string; message: string } | null>(null);
  const [confirming, setConfirming] = useState<OrganizationAccountOption | null>(null);
  /** The copy that the connect step runs for, so the source row keeps its spinner until the connection exists. */
  const [copied, setCopied] = useState<{ sourceId: string; copyId: string } | null>(null);

  const pick = useMutation(
    orpc.accountCopies.pick.mutationOptions({
      onSuccess: ({ copyAccountId }, variables) => {
        void queryClient.invalidateQueries({ queryKey: orpc.accountCopies.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
        setConfirming(null);
        setCopied({ sourceId: variables.accountId, copyId: copyAccountId });
        options.connectCopy({ accountId: copyAccountId });
      },
      onError: (err, variables) => {
        setConfirming(null);
        setCopyFailure({
          accountId: variables.accountId,
          message: orpcErrorMessage(err, 'Could not copy this account'),
        });
      },
      onSettled: () => toast.dismiss(COPYING_TOAST_ID),
    }),
  );

  const copy = (option: OrganizationAccountOption): void => {
    setCopyFailure(null);
    setCopied(null);
    toast.loading(COPYING_MESSAGE, { id: COPYING_TOAST_ID });
    pick.mutate({ accountId: option.accountId });
  };

  const pickingId = pick.isPending ? (pick.variables?.accountId ?? null) : null;
  const connectingAccountId =
    pickingId ?? (copied !== null && options.connectingCopyId === copied.copyId ? copied.sourceId : null);

  return {
    options: query.data?.accounts ?? NO_OPTIONS,
    isLoading: options.enabled && query.isPending,
    unavailable: query.isError
      ? orpcErrorMessage(query.error, 'Could not read the accounts of other workspaces.')
      : null,
    readableWorkspaceCount: query.data?.readableWorkspaceCount ?? null,
    connect: (option) => {
      if (option.iamTrustPolicy) setConfirming(option);
      else copy(option);
    },
    connectingAccountId,
    copyError: (accountId) => (copyFailure?.accountId === accountId ? copyFailure.message : null),
    confirming,
    confirm: () => {
      if (confirming) copy(confirming);
    },
    cancelConfirm: () => {
      if (!pick.isPending) setConfirming(null);
    },
    copyPending: pick.isPending,
  };
}
